/**
 * Token storage key
 */
const TOKEN_KEY = 'authToken';

/**
 * Token data structure
 */
interface TokenData {
  token: string;
  expireTime?: number;
  autoLogin?: boolean;
}

/**
 * Save token based on autoLogin setting
 * @param token - The authentication token
 * @param autoLogin - Whether to persist token across browser sessions
 * @param expireTime - Optional token expiration time (timestamp)
 */
export function saveToken(token: string, autoLogin: boolean, expireTime?: number): void {
  const tokenData: TokenData = {
    token,
    expireTime,
    autoLogin,
  };

  if (autoLogin) {
    // Save to localStorage for persistent storage (survives browser close)
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokenData));
    sessionStorage.removeItem(TOKEN_KEY);
  } else {
    // Save to sessionStorage for temporary storage (cleared when browser closes)
    sessionStorage.setItem(TOKEN_KEY, JSON.stringify(tokenData));
    localStorage.removeItem(TOKEN_KEY);
  }
}

/**
 * Get token from storage
 * Priority: localStorage (auto-login) > sessionStorage (session-only)
 * @returns The token string or null if not found/expired
 */
export function getToken(): string | null {
  // First check localStorage (auto-login users)
  const localData = localStorage.getItem(TOKEN_KEY);
  if (localData) {
    try {
      const tokenData: TokenData = JSON.parse(localData);
      // Check if token is expired
      if (tokenData.expireTime && Date.now() > tokenData.expireTime) {
        clearToken();
        return null;
      }
      return tokenData.token;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
    }
  }

  // Then check sessionStorage (session-only users)
  const sessionData = sessionStorage.getItem(TOKEN_KEY);
  if (sessionData) {
    try {
      const tokenData: TokenData = JSON.parse(sessionData);
      // Check if token is expired
      if (tokenData.expireTime && Date.now() > tokenData.expireTime) {
        clearToken();
        return null;
      }
      return tokenData.token;
    } catch {
      sessionStorage.removeItem(TOKEN_KEY);
    }
  }

  return null;
}

/**
 * Get full token data from storage
 * @returns The token data object or null if not found
 */
export function getTokenData(): TokenData | null {
  const localData = localStorage.getItem(TOKEN_KEY);
  if (localData) {
    try {
      return JSON.parse(localData);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
    }
  }

  const sessionData = sessionStorage.getItem(TOKEN_KEY);
  if (sessionData) {
    try {
      return JSON.parse(sessionData);
    } catch {
      sessionStorage.removeItem(TOKEN_KEY);
    }
  }

  return null;
}

/**
 * Clear token from both storage types
 */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
}

/**
 * Check if user is authenticated (has valid token)
 * @returns True if authenticated, false otherwise
 */
export function isAuthenticated(): boolean {
  return !!getToken();
}

/**
 * Check if current session is auto-login enabled
 * @returns True if auto-login is enabled, false otherwise
 */
export function isAutoLogin(): boolean {
  const tokenData = getTokenData();
  return tokenData?.autoLogin ?? false;
}
